<template>
  <div class="tw:flex tw:flex-nowrap tw:text-xxs">
    <div class="tw:mr-0.5">
      <QIcon
        class="text-ds-denim-9 tw:mt-[1px]"
        name="ion-ios-people"
        size="23px"
      />
    </div>

    <div>
      {{ labelText }}

      <div class="tw:text-left tw:text-xxs tw:font-bold">
        {{ suffix(viewsCount) }}
      </div>
    </div>

    <BaseStreemTooltip>
      {{ suffix(viewsCount) }} {{ tooltip }}
    </BaseStreemTooltip>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseStreemTooltip } from "shared/components/base";
import { suffix } from "shared/helpers/number";
import type { Mention } from "shared/types/mentions";
import { MentionType } from "shared/types/mentions";

export interface SocialViewCountWidgetProps {
  mention: Mention;
}

const props = defineProps<SocialViewCountWidgetProps>();

const isBlogPost = computed(() => props.mention.type === MentionType.blog_post);
const isTwitter = computed(() => props.mention.type === MentionType.tweet);

const isYouTube = computed(
  () => props.mention.type === MentionType.youtube_video
);

const isExordeReddit = computed(() =>
  ["exorde_reddit_post", "exorde_reddit_post_comment"].includes(
    props.mention.type
  )
);

const isReddit = computed(() =>
  ["reddit_post", "reddit_post_comment"].includes(props.mention.type)
);

const isTiktok = computed(() =>
  ["tiktok_video", "tiktok_comment"].includes(props.mention.type)
);

const isBluesky = computed(
  () => props.mention.type === MentionType.bluesky_post
);

const viewsCount = computed(() => {
  if (isTwitter.value || isTiktok.value || isExordeReddit.value) {
    return props.mention.author_follower_count;
  }

  if (isYouTube.value) {
    return props.mention.view_count;
  }

  if (isReddit.value || isBluesky.value) {
    return props.mention.source_follower_count;
  }

  return 0;
});

const tooltip = computed(() => {
  if (isTwitter.value || isBluesky.value) {
    return getLocaleText("social_mention_card.followers");
  }

  if (isReddit.value || isExordeReddit.value) {
    return getLocaleText("social_mention_card.subreddit_followers");
  }

  if (isYouTube.value || isBlogPost.value) {
    return getLocaleText("social_mention_card.views");
  }

  return "";
});

const labelText = computed(() => {
  if (isYouTube.value || isBlogPost.value) {
    return getLocaleText("social_view_count_widget.views_label");
  }

  return getLocaleText("social_view_count_widget.followers_label");
});
</script>
